import { maxFlightPassengerCount } from 'constants/flight'
import { getSumExtraGuestsSurcharge, allRoomsOccupantsFitRoomCapacity } from 'lib/checkout/cartReservationUtils'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { getCalenderDaysByCheckIn } from 'lib/offer/offerCalendarUtils'
import { scheduleIsCurrent, hasTravellableSchedules, hasSellableSchedule } from 'lib/offer/scheduleStatusUtils'
import { addFlightPrice, determineMemberPricing, onlyNonZeroFees } from 'lib/offer/pricing'
import getValueOffPercent from 'lib/offer/getValueOffPercent'
import { isMaldivesCharterBundleOffer } from 'constants/charterFlight'

function mapLoyaltyUpgradeAvailabilityView(
  offer: App.HotelOffer | App.VillaOffer | App.BundleOffer,
  rooms: Array<App.Occupants>,
  options: { day: App.CalendarDay, pkg?: never } | { pkg: App.HotelPackage, day?: never },
): App.OfferPackageOptionLoyaltyUpgradeAvailabilityView | undefined {
  const { pkg, day } = options
  const loyaltyUniqueKeys = day?.loyalty.availableUpgradeTargetsUniqueKeys ?? pkg?.loyalty?.upgradeTargetsUniqueKeys
  const roomUpgradePackageUniqueKey = loyaltyUniqueKeys?.room
  const roomUpgradePackage = roomUpgradePackageUniqueKey ? offer.packages.find(pkg => pkg.uniqueKey === roomUpgradePackageUniqueKey) : undefined
  const isRoomUpgradeCapacityValid = !!roomUpgradePackage && (!rooms || rooms.every(room => allRoomsOccupantsFitRoomCapacity(room, roomUpgradePackage.roomRate?.capacities ?? [], offer)))
  const packageUpgradePackageUniqueKey = loyaltyUniqueKeys?.package
  const packageUpgradePackage = packageUpgradePackageUniqueKey ? offer.packages.find(pkg => pkg.uniqueKey === packageUpgradePackageUniqueKey) : undefined
  const isPackageUpgradeCapacityValid = !!packageUpgradePackage && (!rooms || rooms.every(room => allRoomsOccupantsFitRoomCapacity(room, packageUpgradePackage.roomRate?.capacities ?? [], offer)))

  if (!isRoomUpgradeCapacityValid && !isPackageUpgradeCapacityValid) return

  if (isRoomUpgradeCapacityValid && isPackageUpgradeCapacityValid) {
    return {
      upgradeType: 'room-and-package',
      roomUpgradePackage,
      packageUpgradePackage,
    }
  }

  if (isRoomUpgradeCapacityValid && !isPackageUpgradeCapacityValid) {
    return {
      upgradeType: 'room-only',
      roomUpgradePackage,
    }
  }

  if (isPackageUpgradeCapacityValid && !isRoomUpgradeCapacityValid) {
    return {
      upgradeType: 'package-only',
      packageUpgradePackage,
    }
  }
}

/**
 * Creates an offer page package view for the pkg and offer given
 * This view represents the price and availability values for the
 * given package.
 */
export function mapPackageView(
  offer: App.HotelOffer | App.VillaOffer | App.BundleOffer,
  pkg: App.HotelPackage,
  rooms: Array<App.Occupants>,
  options: {
    calendar?: App.Calendar
    airport?: App.Airport
    luxPlusMember?: boolean
    checkIn?: string
    rate?: App.OfferAvailableRate
  } = {},
): App.OfferPackageOptionView {
  const { airport, calendar, luxPlusMember, checkIn, rate } = options
  const numberOfRooms = rooms.length || 1
  const useDynamicRates = !!offer.property?.useDynamicCancellationPolicies
  const isCapacityValid = !rooms || rooms.every(room => allRoomsOccupantsFitRoomCapacity(room, pkg.roomRate?.capacities ?? [], offer))

  let loyaltyUpgradeAvailability = mapLoyaltyUpgradeAvailabilityView(offer, rooms, { pkg })

  let day = calendar?.cheapestDay
  let flightsDay = calendar?.cheapestDayWithFlights
  if (checkIn && calendar) {
    const selectedDay = getCalenderDaysByCheckIn(calendar)[checkIn]
    day = selectedDay ?? calendar.cheapestDay
    flightsDay = selectedDay ?? calendar.cheapestDayWithFlights
    loyaltyUpgradeAvailability = mapLoyaltyUpgradeAvailabilityView(offer, rooms, { day })
  }

  // our set of values values that are filled in based on the information we are given
  const baseIsAvailable = scheduleIsCurrent(offer.onlinePurchaseSchedule) && !!pkg.price
  let basePrice = 0
  let baseMemberPrice = 0
  let baseValue = 0
  let baseMemberValue = 0
  let surcharge = 0
  let taxesAndFees = 0
  let propertyFees = 0
  let flightPrice = 0
  let hotelAvailable = false
  let flightsAvailable = false
  let extraGuestsSurcharge = 0
  // availableRooms is undefined until we have rate or calendar data
  // This allows consumers to distinguish between "loading" (undefined) and "sold out" (0)
  let availableRooms: number | undefined
  let refundable = false

  if (useDynamicRates && rate) {
    basePrice = rate.hotelPrice
    baseMemberPrice = rate.hotelMemberPrice ?? pkg.memberPrice * numberOfRooms
    baseValue = rate.hotelValue
    baseMemberValue = rate.hotelMemberValue ?? pkg.memberValue * numberOfRooms
    surcharge = rate.surcharge
    taxesAndFees = rate.taxesAndFees
    propertyFees = rate.propertyFees
    flightPrice = rate.flightPrice || day?.flightPrice || 0
    hotelAvailable = baseIsAvailable && (day?.hotelAvailable ?? true) && hasTravellableSchedules(pkg.schedules, checkIn!)
    flightsAvailable = hotelAvailable && (day?.flightsAvailable ?? true)
    extraGuestsSurcharge = rate.extraGuestSurcharge
    availableRooms = rate.availableRooms
    refundable = !!rate.cancellationPolicies?.policies.some(policy => !policy.nonRefundable)
  } else if (day) {
    basePrice = day.hotelPrice
    baseMemberPrice = day.hotelMemberPrice ?? pkg.memberPrice * numberOfRooms
    baseValue = day.hotelValue
    baseMemberValue = day.hotelMemberValue ?? pkg.memberValue * numberOfRooms
    surcharge = day.surcharge
    taxesAndFees = day.taxesAndFees
    propertyFees = day.propertyFees
    flightPrice = (!airport ? 0 : flightsDay?.flightPrice) ?? 0
    hotelAvailable = baseIsAvailable && (day.hotelAvailable ?? true) && hasSellableSchedule(pkg.roomRate?.schedules ?? [])
    flightsAvailable = hotelAvailable && (day.flightsAvailable ?? false)
    extraGuestsSurcharge = getSumExtraGuestsSurcharge(rooms, offer, pkg)
    // Only use day.availableRooms if we're not using dynamic rates
    // When dynamic rates are enabled, the authoritative source for availability is the rate API
    // If rate is still loading, availableRooms should stay undefined to indicate loading state
    if (!useDynamicRates) {
      availableRooms = day.availableRooms
    }
    refundable = pkg.roomRate?.cancellationPolicy?.type !== 'non-refundable'
  } else {
    basePrice = pkg.price * numberOfRooms
    baseMemberPrice = pkg.memberPrice * numberOfRooms
    baseValue = pkg.value * numberOfRooms
    baseMemberValue = pkg.memberValue * numberOfRooms
    surcharge = pkg.surcharge ?? 0
    taxesAndFees = pkg.taxesAndFees * numberOfRooms
    propertyFees = pkg.propertyFees * numberOfRooms
    flightPrice = !airport ? 0 : offer.flightPrices?.[airport.code] ?? 0
    hotelAvailable = baseIsAvailable && hasSellableSchedule(pkg.roomRate?.schedules ?? [])
    flightsAvailable = hotelAvailable
    extraGuestsSurcharge = getSumExtraGuestsSurcharge(rooms, offer, pkg)
    refundable = pkg.roomRate?.cancellationPolicy?.type !== 'non-refundable'
  }

  // fields calculated from the base values
  const hotelPrice = basePrice + surcharge + extraGuestsSurcharge
  const hotelTotal = hotelPrice + propertyFees
  const hotelMemberPrice = baseMemberPrice ? baseMemberPrice + surcharge + extraGuestsSurcharge : 0
  const hotelValue = baseValue + surcharge + extraGuestsSurcharge
  const hotelMemberValue = baseMemberValue ? baseMemberValue + surcharge + extraGuestsSurcharge : 0
  const hotelMemberTotal = hotelMemberPrice ? hotelMemberPrice + propertyFees : 0
  const hotelPricingValue = hotelValue + propertyFees
  const hotelMemberPricingValue = hotelMemberValue ? hotelMemberValue + propertyFees : 0

  const baseHotelPrice = baseMemberPrice > 0 && luxPlusMember ? hotelMemberPrice : hotelPrice
  const baseHotelValue = baseMemberValue > 0 && luxPlusMember ? hotelMemberValue : hotelValue
  const hotelDiscountPercent = getValueOffPercent(baseHotelValue, baseHotelPrice)

  const isCharterBundle = isMaldivesCharterBundleOffer(offer)
  const baseFlightBundledPrice = baseHotelPrice + flightPrice
  const baseFlightBundledValue = baseHotelValue + flightPrice
  // Maldives charter bundles: the flight leg has no discount of its own, so
  // reuse the hotel-only discount instead of diluting it with the flight price.
  const bundledDiscountPercent = isCharterBundle
    ? hotelDiscountPercent
    : getValueOffPercent(baseFlightBundledValue, baseFlightBundledPrice)

  const totalPassengers = sumUpOccupancies(rooms)

  const pricing: App.PricingWithValue = {
    price: hotelTotal,
    value: hotelPricingValue,
    discountPercent: getValueOffPercent(hotelPricingValue, hotelTotal),
    fees: onlyNonZeroFees([
      { type: 'taxesAndFees', amount: taxesAndFees },
      { type: 'property', amount: propertyFees },
    ]),
    saleUnit: offer.saleUnit,
  }
  const memberPricing = determineMemberPricing(pricing, hotelMemberTotal, hotelMemberPricingValue)

  return {
    package: pkg,
    hotelPrice,
    hotelMemberPrice,
    hotelTotal,
    hotelMemberTotal,
    basePrice,
    baseMemberPrice,
    hotelValue,
    hotelMemberValue,
    taxesAndFees,
    propertyFees,
    baseValue,
    surcharge,
    extraGuestsSurcharge,
    flightPrice,
    capacityValid: isCapacityValid,
    hotelAvailable,
    flightsAvailable: flightsAvailable && totalPassengers <= maxFlightPassengerCount,
    hotelDiscountPercent,
    bundledDiscountPercent,
    fromCalendarInfo: !!calendar,
    hasReturnFareFree: flightsDay?.hasReturnFareFree ?? day?.hasReturnFareFree,
    availableRooms,
    hasAvailableRates: useDynamicRates ? !!rate : hotelAvailable,
    pricing,
    memberPricing,
    pricingWithFlights: flightPrice ? addFlightPrice(pricing, flightPrice, isCharterBundle) : undefined,
    memberPricingWithFlights: (memberPricing && flightPrice) ? addFlightPrice(memberPricing, flightPrice, isCharterBundle) : undefined,
    refundable,
    loyaltyUpgradeAvailability,
  }
}

export default mapPackageView
